/**
 * postMessage wire shapes for the two Sign-in-with-PlaySteady embed surfaces that
 * `PlaySteady.open()` drives, keyed by the token it's given:
 *
 *   - `ltok_…` → the consent surface (`/embed/connect`), `sg.connect.*`:
 *       ready · error · result{ status: linked|denied, public_token }
 *   - `dep_secret_…`   → the deposit surface (`/embed/deposit`), `sg.session.*`:
 *       ready · error · result{ status: succeeded|declined|denied,
 *                                deposit, public_token }
 *
 * The raw access token is NEVER sent over postMessage — only the single-use
 * `public_token`, which the partner exchanges server-to-server.
 */

// ─── shared ──────────────────────────────────────────────────────────────────

/** Why a surface couldn't complete. Shared across both contracts. */
export const PlaySteadyUnavailableReason = {
  /** No `ready` message received within the SDK's timeout. */
  Timeout: 'timeout',
  /** Token was malformed or already consumed. */
  InvalidToken: 'invalid_token',
  /** Token expired before the consumer opened the surface. */
  ExpiredToken: 'expired_token',
  /** Server error inside the surface. */
  ServerError: 'server_error',
  /** Anything not covered above. */
  Unknown: 'unknown',
} as const;
export type PlaySteadyUnavailableReason =
  (typeof PlaySteadyUnavailableReason)[keyof typeof PlaySteadyUnavailableReason];

function isObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object';
}

function isOptionalString(value: unknown): boolean {
  return value === undefined || typeof value === 'string';
}

function isOptionalUserRef(value: unknown): boolean {
  return value === undefined || value === null || typeof value === 'string';
}

// ─── connect surface (ltok_) ───────────────────────────────────────────────

export const CONNECT_READY_MESSAGE_TYPE = 'sg.connect.ready';
export const CONNECT_ERROR_MESSAGE_TYPE = 'sg.connect.error';
export const CONNECT_RESULT_MESSAGE_TYPE = 'sg.connect.result';

export type ConnectStatus = 'linked' | 'denied';

export interface ConnectReadyMessage {
  readonly type: typeof CONNECT_READY_MESSAGE_TYPE;
}

export interface ConnectErrorMessage {
  readonly type: typeof CONNECT_ERROR_MESSAGE_TYPE;
  readonly reason: PlaySteadyUnavailableReason;
  readonly message: string;
}

export interface ConnectResultMessage {
  readonly type: typeof CONNECT_RESULT_MESSAGE_TYPE;
  readonly status: ConnectStatus;
  /** Present only on `status: 'linked'`. Single-use; exchange S2S. */
  readonly public_token?: string;
  /** The partner's own user ref, echoed from link time. */
  readonly user_ref?: string | null;
}

export function isConnectReadyMessage(value: unknown): value is ConnectReadyMessage {
  return isObject(value) && value['type'] === CONNECT_READY_MESSAGE_TYPE;
}

export function isConnectErrorMessage(value: unknown): value is ConnectErrorMessage {
  if (!isObject(value)) return false;
  if (value['type'] !== CONNECT_ERROR_MESSAGE_TYPE) return false;
  if (typeof value['message'] !== 'string') return false;
  return Object.values(PlaySteadyUnavailableReason).includes(value['reason'] as PlaySteadyUnavailableReason);
}

export function isConnectResultMessage(value: unknown): value is ConnectResultMessage {
  if (!isObject(value)) return false;
  if (value['type'] !== CONNECT_RESULT_MESSAGE_TYPE) return false;
  if (value['status'] !== 'linked' && value['status'] !== 'denied') return false;
  if (!isOptionalString(value['public_token'])) return false;
  return isOptionalUserRef(value['user_ref']);
}

// ─── deposit surface (dep_secret_) ─────────────────────────────────────────────────

export const SESSION_READY_MESSAGE_TYPE = 'sg.session.ready';
export const SESSION_ERROR_MESSAGE_TYPE = 'sg.session.error';
export const SESSION_RESULT_MESSAGE_TYPE = 'sg.session.result';

export type SessionStatus = 'succeeded' | 'declined' | 'denied';

export interface SessionDepositRef {
  readonly id: string;
  readonly status: string;
}

export interface SessionReadyMessage {
  readonly type: typeof SESSION_READY_MESSAGE_TYPE;
}

export interface SessionErrorMessage {
  readonly type: typeof SESSION_ERROR_MESSAGE_TYPE;
  readonly reason: PlaySteadyUnavailableReason;
  readonly message: string;
}

export interface SessionResultMessage {
  readonly type: typeof SESSION_RESULT_MESSAGE_TYPE;
  readonly status: SessionStatus;
  /** Present once a deposit row exists (always for succeeded/declined). */
  readonly deposit?: SessionDepositRef;
  /** First-timer linking only — single-use; exchange S2S. */
  readonly public_token?: string;
  /** The partner's own user ref, echoed from session creation. */
  readonly user_ref?: string | null;
}

export function isSessionReadyMessage(value: unknown): value is SessionReadyMessage {
  return isObject(value) && value['type'] === SESSION_READY_MESSAGE_TYPE;
}

export function isSessionErrorMessage(value: unknown): value is SessionErrorMessage {
  if (!isObject(value)) return false;
  if (value['type'] !== SESSION_ERROR_MESSAGE_TYPE) return false;
  if (typeof value['message'] !== 'string') return false;
  return Object.values(PlaySteadyUnavailableReason).includes(value['reason'] as PlaySteadyUnavailableReason);
}

export function isSessionResultMessage(value: unknown): value is SessionResultMessage {
  if (!isObject(value)) return false;
  if (value['type'] !== SESSION_RESULT_MESSAGE_TYPE) return false;
  if (
    value['status'] !== 'succeeded' &&
    value['status'] !== 'declined' &&
    value['status'] !== 'denied'
  ) {
    return false;
  }
  const pi = value['deposit'];
  if (pi !== undefined) {
    if (!isObject(pi) || typeof pi['id'] !== 'string' || typeof pi['status'] !== 'string') {
      return false;
    }
  }
  if (!isOptionalString(value['public_token'])) return false;
  return isOptionalUserRef(value['user_ref']);
}
